import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { navigationService } from 'src/config/rootService';
import {
    CareRecommendationState,
    TopicSelectionGroupSelectionType,
} from 'src/generated/webapp_gql';
import {
    getGoalInterestsSelected,
    isOnboardingCompleted,
    isOnboardingFirstStepCompleted,
} from 'src/modules/onboarding/selectors/onboardingSelectors';
import { getMostRecentCareRecommendation } from 'src/modules/user/selectors/userSelectors';

export const topicSelectionPaths = {
    [TopicSelectionGroupSelectionType.OnboardingFlow]: 'onboarding',
    [TopicSelectionGroupSelectionType.ProfileSettings]: 'profile-settings',
    [TopicSelectionGroupSelectionType.WellbeingReport]: 'wellbeing-report',
};

const onboardingFirstStepRoutes = [
    '/email-preferences',
    '/welcome',
    '/onboarding-privacy',
    `/onboarding-categories/${
        topicSelectionPaths[TopicSelectionGroupSelectionType.OnboardingFlow]
    }`,
];
const onboardingSecondStepRoutes = [
    '/onboarding-wellness',
    '/a-few-questions',
    '/onboarding-survey',
];

export const useOnboardingGuard = () => {
    const careRecommendation = useSelector(getMostRecentCareRecommendation);
    const goalInterestsSelected = useSelector(getGoalInterestsSelected);

    const [guardPassed, setGuardPassed] = useState(false);

    const onboardingFirstStepCompleted = useSelector(isOnboardingFirstStepCompleted);
    const onboardingCompleted = useSelector(isOnboardingCompleted);

    useEffect(() => {
        const currentUrl = window.location.pathname;

        // handle various cases when user has already skipped the onboarding
        if (careRecommendation?.state === CareRecommendationState.BypassedAssessments) {
            // users who decided to skip the onboarding before topics selection should not be able to go to the second part of the onboarding
            if (onboardingSecondStepRoutes.includes(currentUrl) && !goalInterestsSelected) {
                navigationService.replace(onboardingFirstStepRoutes[1]);
            }

            // users who decided to skip the onboarding after topics selection should not be able to go back to the first part of the onboarding
            if (
                !!goalInterestsSelected &&
                (onboardingFirstStepRoutes.includes(currentUrl) ||
                    currentUrl === '/onboarding-wellness')
            ) {
                navigationService.navigate('/');
            }

            setGuardPassed(true);
            return;
        }

        if (onboardingCompleted) {
            navigationService.navigate('/dashboard');

            return;
        }

        let url;

        if (onboardingFirstStepCompleted) {
            // user has already completed first step of the onboarding
            // so we want to take them to the first component of the second step if they are not there yet

            url = !onboardingSecondStepRoutes.includes(currentUrl) && onboardingSecondStepRoutes[0];
        } else {
            // user hasn't completed first step of the onboarding yet
            // so we want to take them to a page from the first step if they are not there yet
            if (!onboardingFirstStepRoutes.includes(currentUrl)) {
                // if there is a care recommendation already created it means that user already saw the first page
                // so we can go straight to the second one
                url = careRecommendation
                    ? onboardingFirstStepRoutes[0]
                    : onboardingFirstStepRoutes[1];
            }
        }

        if (!url) {
            setGuardPassed(true);
        } else {
            navigationService.navigate(url);
        }
    }, [window.location]);

    return guardPassed;
};
